import { Component, EventEmitter, Inject, Input, NgZone, OnInit, OnChanges, Output, ViewChild } from '@angular/core';
import { take } from 'rxjs/operators';
import { DataStateChangeEvent, GridComponent, GridDataResult, RowClassArgs } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, process, State } from '@progress/kendo-data-query';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmDialog } from '../modals/confirm-dialog.component';
import { EmailService } from '../../services/email.service';
import { EmployeeService } from '../../services/employees.service';
import { NotificationService } from '../../services/notification.service';
import { OrderService } from '../../services/order.service';
import { UtilService } from '../../services/util.service';
import { SendEmailDialog } from '../send-email-popup/send-email-popup.component';
import { Employee, QuoteOrder, SearchCarrierSalesAvailableLoads, SearchTagType } from '../../models/models';

@Component({
    selector: 'carrier-sales-loads-grid',
    templateUrl: './carrier-sales-loads-grid.component.html',
    standalone: false
})
export class CarrierSalesLoadsGrid implements OnInit, OnChanges {
    @Input() loads: SearchCarrierSalesAvailableLoads[] = [];
    @Input() viewType: string = "orders";
    @Input() selectedLoad: SearchCarrierSalesAvailableLoads = {} as SearchCarrierSalesAvailableLoads;
    @Input() isShowBulkTender: boolean = false;
    @Output() onClick = new EventEmitter<any>();
    @Output() onRefresh = new EventEmitter<any>();
    @Output() selectBulkTenderLoads = new EventEmitter<any>();

    @ViewChild(GridComponent, { static: false }) public loadsGrid!: GridComponent;

    public isCSRManager: boolean = false;
    public loadStatus: string = "Orders";
    public filteredLoads: SearchCarrierSalesAvailableLoads[] = [];
    public bulkTenderLoads: SearchCarrierSalesAvailableLoads[] = [];
    public tagTypeList: SearchTagType[] = [];
    public carrierReps: Employee[] = [];
    private carrierRepsFilter: any[] = [];
    public isCarrierRepsAssigned: boolean = false;
    private isPreventRowSelected: boolean = false;

    // Carrier Sales Reps Assigned dialog fields
    public quoteOrderLineID: number = 0;
    public assignedLoad: SearchCarrierSalesAvailableLoads = {} as SearchCarrierSalesAvailableLoads;

    public gridState: State = { skip: 0, take: 40, sort: [{ dir: 'asc', field: 'arrivalStartDate' }] };
    //public gridState: State = { sort: [{ dir: 'asc', field: 'arrivalStartDate' }] };
    public gridData: GridDataResult = { data: [], total: 0 };
    //public pageSizes: number[] = [5, 10, 20, 50, 100, 200, 500];

    constructor(private emailService: EmailService, private employeeService: EmployeeService, public dialog: MatDialog, private ngZone: NgZone,
        private notificationService: NotificationService, private orderService: OrderService, private utilService: UtilService,
        @Inject('BASE_URL') private baseUrl: string) {
    }

    ngOnInit() {
        this.orderService.getSearchTagTypes().subscribe(result => {
            this.tagTypeList = result.resultCollection;
        });
        this.isCSRManager = this.getIsCSRManager();
        this.getCarrierReps();
        this.updateLoads();
        //console.log("loads", this.loads.length);
        //console.log("ngOnInit", this.isShowBulkTender);
    }

    ngOnChanges() {
        if (this.isShowBulkTender != true) {
            this.bulkTenderLoads.length = 0;
            this.gridState.skip = 0;
            this.gridState.take = 40;
            this.filteredLoads.forEach(t => t.isBulkTender = false);
        }
        this.updateLoads();
        //console.log("ngOnChanges", this.isShowBulkTender);
    }

    private getIsCSRManager() {
        var employeeRoles = localStorage.getItem('EmployeeRoles');

        //console.log(employeeRoles);
        if (employeeRoles != null && employeeRoles.length > 0) {
            if (employeeRoles.indexOf('Carrier Sales Manager') > -1) return true;
        }

        return false;
    }

    private getCarrierReps() {
        this.utilService.isLoading = true;
        this.employeeService.getEmployeesByGroupName('carrierrep').subscribe(result => {
            this.utilService.isLoading = false;
            this.carrierReps = result;
        });
    }

    private updateLoads() {
        this.filteredLoads = Object.assign([], this.loads);

        if (this.viewType == "orders") {
            this.loadStatus = "Orders";
            this.filteredLoads = this.filteredLoads.filter(f =>
                (f.loadType == "ORD" || f.loadType == "CG")
                && f.carrierID == 0
            );
        }
        else if (this.viewType == "booked") {
            this.loadStatus = "Booked";
        }
        else if (this.viewType == "quotes") {
            this.loadStatus = "Quotes";
            this.filteredLoads = this.filteredLoads.filter(f =>
                (f.loadType == "OQ" || f.loadType == "QO")
                && f.finalCost == 0
            );
        }
        else if (this.viewType == "rfq") {
            this.loadStatus = "RFQ";
            //this.filteredLoads = this.filteredLoads.filter(f =>
            //    f.loadType == "RFQ" && f.finalCost == 0
            //    || f.loadType == "RFQP"
            //);
        }
        else if (this.viewType == "open-dedicated") {
            this.loadStatus = "Open Dedicated";
            this.filteredLoads = this.filteredLoads.filter(f =>
                f.loadType == "OD"
                && f.finalCost == 0
            );
        }
        this.gridData = process(this.filteredLoads, this.gridState);
        this.fitColumns();
        //console.log("updateLoads", this.viewType, this.filteredLoads);
    }

    public filterChange(filter: CompositeFilterDescriptor): void {
        console.log("this.gridState.filter", this.gridState.filter);
        this.gridState.filter = filter;
        this.carrierRepsChange(this.carrierRepsFilter);
        //this.countryChange(this.originCountryFilter, "origin");
        //this.countryChange(this.destinationCountryFilter, "destination");
        //this.c2cChange(this.c2cFilter);
        ////console.log("filterChange", filter, this.carrierRepsFilter);
        //this.setTrackingBoardGrid();
        this.updateLoads();
    }

    private fitColumns() {
        this.ngZone.onStable.asObservable().pipe(take(1)).subscribe(() => {
            if (this.loadsGrid == null) return;
            this.loadsGrid.autoFitColumns();
        });
    }

    public carrierRepsChange(values: any[]): void {
        //console.log("carrierRepsChange", values);
        this.carrierRepsFilter = values;
        var valMap = values.map(value => ({
            field: "bookedByCarrierRepName",
            operator: "eq",
            value
        }));
        var cf: CompositeFilterDescriptor = {
            filters: valMap,
            logic: "or"
        }

        if (!this.gridState.filter) {
            this.gridState.filter = {
                filters: [
                    {
                        filters: valMap,
                        logic: "or"
                    }
                ],
                logic: "and"
            };
        }
        else {
            var currFilters = this.gridState.filter.filters;
            //console.log("carrierRepsChange:currFilters before", currFilters);
            var idx = -1;
            currFilters.forEach(function (f: CompositeFilterDescriptor | FilterDescriptor, index) {
                var cf = f as CompositeFilterDescriptor;
                if (cf.filters) {
                    var fd = cf.filters[0] as FilterDescriptor;
                    if (fd.field == "bookedByCarrierRepName") {
                        idx = index;
                    }
                    //console.log("carrierRepsChange:ComponsiteFilterDescriptor", cf.filters);
                }
            });
            // Remove the componsite filter if no other filters within the composite filter are in place
            if (idx >= 0) {
                currFilters.splice(idx, 1);
            }
            // Add the composite filter if value(s) selected
            if (values.length > 0) {
                currFilters.push(cf);
            }
            //console.log("carrierRepsChange:currFilters after", currFilters);
        }

        //console.log("carrierRepsChange:this.trackingBoardState.filter", this.trackingBoardState.filter, values, this.carrierRepsFilter);
        this.updateLoads();
    }

    public carrierRepsFilters(filter: CompositeFilterDescriptor): FilterDescriptor[] {
        //this.carrierRepsFilter.splice(0, this.carrierRepsFilter.length, ...flatten(filter).map(({ value }) => value));
        //console.log("carrierRepsFilters", filter, this.carrierRepsFilter);
        return this.carrierRepsFilter;
    }

    public dataStateChange(state: DataStateChangeEvent): void {
        if (this.bulkTenderLoads.length == 0) {
            this.gridState = state;
            this.updateLoads();
            //this.gridData = process(this.loads, this.gridState);
            //this.fitColumns();
        }
    }

    public loadClick(load: SearchCarrierSalesAvailableLoads) {
        //console.log("carrier-sales-loads-grid.loadClick", load);
        //if (this.bulkTenderLoads.length == 0) {
        //    this.bulkTenderLoads.push(load);
        //}
        //this.onClick.emit(this.bulkTenderLoads);
        this.onClick.emit(load);
    }

    public rowSelected(e: any) {
        console.log("carrier-sales-loads-grid.rowSelected", e, this.assignedLoad, this.bulkTenderLoads, this.isShowBulkTender);
        //if (this.isShowBulkTender != true) {
        //    this.onClick.emit(load);
        //}
        if (this.isShowBulkTender != true && e.selectedRows && !this.assignedLoad.quoteOrderLineID && !this.isPreventRowSelected) {
            // Only fire if clicking the row and not a link or button in the row
            var load = e.selectedRows[0].dataItem;
            this.selectedLoad = load;
            //console.log("carrier-sales-loads-grid.rowSelected", e, load);

            this.loadClick(load);
        }
        //this.onClick.emit(load);
    }

    public rowCallback(context: RowClassArgs) {
        const isHighPriority = (context.dataItem.orderPriority == "High");
        const isExpedite = (context.dataItem.rateIndex == "Expedite");
        const isCG = (context.dataItem.erpAssignedTo == "CG" || context.dataItem.loadType == "CG");
        const isRFQP = (context.dataItem.loadType == "RFQP");
        const isRFQ = (context.dataItem.loadType == "RFQ");
        //return {
        //    danger: isHighPriority
        //}
        if (isRFQ || isRFQP) {
            var rowClass = context.dataItem.opportunityPriorityColorClass;
            if (rowClass == "danger") {
                return {
                    danger: true
                }
            }
            else if (rowClass == "warning") {
                return {
                    warning: true
                }
            }
            else if (rowClass == "success") {
                return {
                    success: true
                }
            }
        }
        else {
            if (isExpedite) {
                return {
                    expedite: isExpedite
                }
            }
            else if (isHighPriority) {
                return {
                    highPriority: isHighPriority
                }
            }
            else if (isCG) {
                return {
                    warning: isCG
                }
            }
            else if (isRFQP) {
                return {
                    warning: isRFQP
                }
            }
        }
    }

    //public rowCallback(context: RowClassArgs) {
    //    const isHighPriority = (context.dataItem.orderPriority == "High");
    //    const isRFQP = (context.dataItem.loadType == "RFQP");
    //    return {
    //        danger: isHighPriority,
    //        warning: isRFQP
    //    }
    //}

    public toggleBulkTender(load: SearchCarrierSalesAvailableLoads) {
        //console.log("toggleBulkTender", load.isBulkTender);
        if (load.isBulkTender == true) {
            load.isBulkTender = false;
        }
        else {
            load.isBulkTender = true;
        }
        var isChecked = (load.isBulkTender == true);
        if (isChecked) {
            this.bulkTenderLoads.length = 0;
            this.filteredLoads.forEach(t => t.isBulkTender = false);
        }
        var loads: SearchCarrierSalesAvailableLoads[] = Object.assign([], this.filteredLoads);
        //console.log("toggleBulkTender:checked", load.isBulkTender, isChecked);
        if (isChecked) {
            loads = loads.filter(t =>
                t.billTo == load.billTo
                && t.geocodeLaneID == load.geocodeLaneID
                && t.originCityID == load.originCityID
                && t.destinationCityID == load.destinationCityID
                //&& t.trailerTypeID == load.trailerTypeID
                //&& t.modeID == load.modeID
                //&& t.rateIndex == load.rateIndex
            );
            loads.forEach(t => t.isBulkTender = true);
            this.bulkTenderLoads = Object.assign([], loads);
            //console.log("toggleBulkTender:checked", loads);
        }
        else {
            this.bulkTenderLoads = this.bulkTenderLoads.filter(t => t.quoteOrderLineID != load.quoteOrderLineID);
        }
        if (isChecked) {
            this.gridState.skip = 0;
            //this.gridState.take = undefined;
            this.gridState.take = loads.length;
            this.gridData = process(loads, this.gridState);
        }

        //console.log("toggleBulkTender:bulkTenderLoads", this.bulkTenderLoads);
        this.selectBulkTenderLoads.emit(this.bulkTenderLoads);
        //console.log("toggleBulkTender", loads);
    }

    public updateLoad(quoteOrderLineID: number, load: SearchCarrierSalesAvailableLoads, evt: any) {
        console.log("updateLoad", quoteOrderLineID, evt.target.value, load, evt);
        this.orderService.saveLineFromSearchCarrierSalesAvailableLoads(load).subscribe(result => {
            console.log("updateLoad", result);
        });
    //    for (var i = 0; i < this.quoteOrderLines.length; i++) {
    //        this.orderService.saveLineFromSearchQuoteOrderLine(this.quoteOrderLines[i]).subscribe(result => {
    //            var ok = result;
    //            if (i == this.quoteOrderLines.length) {
    //            }
    //        });
    //    }
    //    this.saveComment(this.notes);
    //    this.saveComment(this.remarks);
    }

    public setQuoteOrderLineLoadStatus(load: SearchCarrierSalesAvailableLoads, loadStatus: string) {
        this.isPreventRowSelected = true;
        //console.log("setQuoteOrderLineLoadStatus", line.quoteOrderLineID, line);

        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            header: 'Change Load Status',
            message: 'You are about to set this load to ' + loadStatus + ': Load #' + load.legNumber + '.  Confirm to continue.'
        }

        const dialogRef = this.dialog.open(ConfirmDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(dialogResult => {
            this.isPreventRowSelected = false;

            if (!dialogResult) {
                return;
            };

            this.utilService.isLoading = true;
            this.orderService.setQuoteOrderLineLoadStatus(load.quoteOrderLineID, loadStatus).subscribe(result => {
                this.utilService.isLoading = false;
                if (result.message != "OK") {
                    this.notificationService.showError("Error setting load status " + loadStatus + ": " + result.message, "ERROR: Set Load Status");
                }
                else {
                    //this.sendLoadStatusChangeEmail(load, loadStatus);
                    this.notificationService.showSuccess("Successfully set load status " + loadStatus, "SUCCESS: Set Load Status");
                    console.log("setQuoteOrderLineLoadStatus", loadStatus, result, load);
                }
            });
        });
    }

    public setQuoteOrderLineRateIndex(load: SearchCarrierSalesAvailableLoads, rateIndex: string) {
        this.isPreventRowSelected = true;
        console.log("setQuoteOrderRateIndex", load.quoteOrderLineID, load);

        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            header: 'Change Rate Index',
            message: 'You are about to set this load to ' + rateIndex + ': Load #' + load.legNumber + '.  Confirm to continue.'
        }

        const dialogRef = this.dialog.open(ConfirmDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(dialogResult => {
            this.isPreventRowSelected = false;
            if (!dialogResult) return;

            this.utilService.isLoading = true;
            this.orderService.setQuoteOrderLineRateIndex(load.quoteOrderLineID, rateIndex).subscribe(result => {
                this.utilService.isLoading = false;
                if (result.message != "OK") {
                    this.notificationService.showError("Error setting rate index " + rateIndex + ": " + result.message, "ERROR: Set Rate Index");
                }
                else {
                    this.notificationService.showSuccess("Successfully set rate index " + rateIndex, "SUCCESS: Set Rate Index");
                    console.log("setQuoteOrderLineRateIndex", rateIndex, result, load);
                }
                this.sendRateIndexChangeEmail(load, "Planned");
            });
        });
    }

    public sendRateIndexChangeEmail(load: SearchCarrierSalesAvailableLoads, loadStatus: string) {
        this.orderService.getQuoteOrderByID(load.quoteOrderID).subscribe(orderResult => {
            if (orderResult.resultCollection.length > 0) {
                var order: QuoteOrder = orderResult.resultCollection[0];
                var orderOwnerEmployeeID: number = order.orderOwnerEmployeeID;
                //var emp: Employee = this.employees.find(t => t.employeeId = orderOwnerEmployeeID)!;
                this.employeeService.getEmployeeById(orderOwnerEmployeeID).subscribe(empResult => {
                    var emp: Employee = empResult;
                    var earliestDate: Date = new Date(load.earliestStartDateTime!);
                    var dateTimeString: string = earliestDate.getMonth() + "/" + earliestDate.getDate() + "/" + earliestDate.getFullYear();
                    dateTimeString += " " + ("0" + earliestDate.getHours()).slice(-2) + ":" + ("0" + earliestDate.getMinutes()).slice(-2);
                    var emailTo: string = emp.email;
                    var orderUrl: string = this.baseUrl + "#/order-entry/" + load.quoteOrderID;
                    var emailSubject: string = "Contracted carrier cannot cover: Order #" + load.orderNumber + "-" + load.legNumber;
                    emailSubject += " - " + load.originCity + ", " + load.originState + " to " + load.destinationCity + ", " + load.destinationState;
                    var emailBody: string = "<p>The contracted carrier cannot cover this load and is moving to a spot load: ";
                    emailBody += "Order #<a href='" + orderUrl + "'>" + load.orderNumber + "-" + load.legNumber + "</a>.";
                    emailBody += "<br />";
                    emailBody += load.originCity + ", " + load.originState + " to " + load.destinationCity + ", " + load.destinationState;
                    emailBody += "<br />";
                    emailBody += dateTimeString;
                    emailBody += "</p>";

                    const dialogConfig = new MatDialogConfig();
                    dialogConfig.disableClose = false;
                    dialogConfig.autoFocus = true;
                    dialogConfig.width = '400px',
                        dialogConfig.data = {
                            showEmailBody: false,
                            showSubject: false,
                            //contacts: contacts,
                            emailTo: emailTo,
                            emailCC: "",
                            ccEmails: "",
                            emailSubject: emailSubject,
                            emailBody: emailBody
                        }

                    const dialogRef = this.dialog.open(SendEmailDialog, dialogConfig);

                    //console.log("sendOfferEmail", accountRep, offer);

                    dialogRef.componentInstance.emailTo = emailTo;
                    dialogRef.componentInstance.emailCC = "";
                    dialogRef.componentInstance.ccEmails = [];
                    dialogRef.componentInstance.emailSubject = emailSubject;
                    dialogRef.componentInstance.emailBody = emailBody;
                    //dialogRef.componentInstance.notes = dataItem.comment;

                    dialogRef.afterClosed().subscribe(result => {
                        //console.log(result);
                        if (result.btnOK == true) {
                            this.utilService.isLoading = true;
                            this.emailService.sendEmail(result.emailTo, result.emailCC, "", 0, result.emailSubject, result.emailBody).subscribe(result => {
                                this.utilService.isLoading = false;
                                var ok = result;
                            });
                        }
                    });

                    this.onRefresh.emit();
                });
            }
            else {
                this.notificationService.showError("Error loading order #" + load.orderNumber + " for sending email.", "ERROR: Load Order");
            }
        });
    }

    public sendAvailableLoadOfferEmail(load: SearchCarrierSalesAvailableLoads) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.width = "400px",
        dialogConfig.data = {
            showEmailBody: false,
            showSubject: false,
            //contacts: contacts,
            emailTo: "",
            emailCC: "",
            ccEmails: "",
            emailSubject: "",
            emailBody: ""
        }

        const dialogRef = this.dialog.open(SendEmailDialog, dialogConfig);

        //console.log("sendOfferEmail", accountRep, offer);

        dialogRef.componentInstance.emailTo = "";
        dialogRef.componentInstance.emailCC = "";
        dialogRef.componentInstance.ccEmails = [];
        dialogRef.componentInstance.emailSubject = "";
        dialogRef.componentInstance.emailBody = "";
        //dialogRef.componentInstance.notes = dataItem.comment;

        dialogRef.afterClosed().subscribe(result => {
            //console.log(result);
            if (result.btnOK == true) {
                //this.utilService.isLoading = true;
                console.log("sendAvailableLoadOfferEmail", load.quoteOrderLineID, result);
                this.emailService.sendAvailableLoadOfferEmail(load.quoteOrderLineID, result.emailTo, result.emailCC).subscribe(result => {
                    this.utilService.isLoading = false;
                    var ok = result;
                });
            }
        });
    }

    public editCarrierReps(load: SearchCarrierSalesAvailableLoads) {
        this.assignedLoad = Object.assign({}, load);
    }

    public cancelDialogClick() {
        var emptyLoad: SearchCarrierSalesAvailableLoads = {} as SearchCarrierSalesAvailableLoads;
        this.assignedLoad = emptyLoad;
    }

    public getTagTypes(idList: string) {
        var tagTypes: SearchTagType[] = [];
        if (idList && idList != "") {
            var arr: number[] = [];
            arr = idList.replace(/, +/g, ",").split(",").map(Number);
            for (var i = 0; i < arr.length; i++) {
                var idx: number = this.tagTypeList.findIndex(t => t.tagTypeID == arr[i]);
                if (idx >= 0) {
                    tagTypes.push(this.tagTypeList[idx]);
                }
            }
        }
        return tagTypes;
    }
}
